import React, { useEffect, useState } from "react";
import axios from "axios";
import { GrStatusUnknown } from "react-icons/gr";
import { MdDateRange, MdTrain } from "react-icons/md";
import {
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiLayers,
  FiRefreshCw,
  FiAlertCircle,
} from "react-icons/fi";
import { GoDotFill } from "react-icons/go";

import { getCurrentDate } from "../../Utilitie";
import Loading from "../../Extra/Loading";

const Dates = ({ setSelectedDate, selectedDate, dataChanged }) => {
  const urlPrefixBlock = process.env.REACT_APP_URL_BLOCK_PREFIX;

  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [date, setDate] = useState(() => getCurrentDate());

  const fetchDatesData = async () => {
    const dateForFetch = date || getCurrentDate();

    setLoading(true);
    setError(false);

    try {
      const response = await axios.get(
        `${urlPrefixBlock}getblockdataReportFor30Days?date=${dateForFetch}`,
      );

      if (response.data?.status === "ok") {
        const result = Array.isArray(response.data.data)
          ? response.data.data
          : [];

        setDates(result);

        // Select the first date only when no date is already selected.
        if (!selectedDate && result.length > 0) {
          setSelectedDate(result[0]);
        }
      } else {
        setDates([]);
        setError(true);
      }
    } catch (err) {
      console.error("Failed to fetch block date report:", err);
      setDates([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDatesData();
    // Refresh when the base date or underlying data changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, dataChanged]);

  const getStatus = (item) => {
    const entries = Number(item.no_of_entries) || 0;
    const updated = Number(item.no_of_updated_entries) || 0;

    if (entries === 0) {
      return {
        label: "No Entries",
        color: "#64748b",
        background: "#f1f5f9",
        border: "#cbd5e1",
        icon: <FiAlertCircle />,
      };
    }

    if (entries === updated) {
      return {
        label: "Completed",
        color: "#15803d",
        background: "#f0fdf4",
        border: "#86efac",
        icon: <FiCheckCircle />,
      };
    }

    if (updated === 0) {
      return {
        label: "Pending",
        color: "#b45309",
        background: "#fffbeb",
        border: "#fcd34d",
        icon: <FiClock />,
      };
    }

    return {
      label: "Partially Updated",
      color: "#0369a1",
      background: "#f0f9ff",
      border: "#7dd3fc",
      icon: <FiRefreshCw />,
    };
  };

  const isSelected = (item) => selectedDate?.date === item.date;

  const handleDateSelect = (item) => {
    if (isSelected(item)) return;

    const confirmChange = selectedDate?.date
      ? window.confirm(
          `Do you want to change the date from ${selectedDate.date} to ${item.date}?`,
        )
      : true;

    if (confirmChange) {
      setSelectedDate(item);
    }
  };

  return (
    <div className="container-fluid px-2 px-md-3 py-3">
      {/* Header */}
      <div
        className="card border-0 shadow-sm mb-3"
        style={{ borderRadius: "14px", overflow: "hidden" }}
      >
        <div
          className="card-body p-3 p-md-4"
          style={{
            background:
              "linear-gradient(110deg, #063b87 0%, #0a57bb 65%, #087344 100%)",
          }}
        >
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                style={{
                  width: "48px",
                  height: "48px",
                  background: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  fontSize: "24px",
                }}
              >
                <MdTrain />
              </div>

              <div>
                <h5 className="text-white fw-bold mb-1">Block Entry Status</h5>
                <div className="text-white-50 small">
                  Last 30 days report · Select a date to view its records
                </div>
              </div>
            </div>

            <div
              className="d-flex align-items-center gap-2 rounded-3 p-2"
              style={{ background: "rgba(255,255,255,0.12)" }}
            >
              <FiCalendar className="text-white flex-shrink-0" size={18} />

              <input
                type="date"
                className="form-control form-control-sm border-0"
                aria-label="Select report date"
                value={date}
                max={getCurrentDate()}
                onChange={(e) => setDate(e.target.value)}
                style={{
                  minWidth: "145px",
                  background: "#fff",
                  color: "#063b87",
                  fontWeight: 600,
                }}
              />

              <button
                type="button"
                className="btn btn-sm btn-light d-flex align-items-center gap-1"
                title="Refresh report"
                onClick={fetchDatesData}
                disabled={loading}
              >
                <FiRefreshCw className={loading ? " " : ""} />
                <span className="d-none d-sm-inline">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Date report */}
      <div className="card border-0 shadow-sm" style={{ borderRadius: "14px" }}>
        <div className="card-header bg-white border-bottom px-3 px-md-4 py-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <MdDateRange size={22} style={{ color: "#0a57bb" }} />
              <h6 className="fw-bold mb-0" style={{ color: "#16365c" }}>
                Daily Entry Report
              </h6>
            </div>

            <span
              className="badge rounded-pill px-3 py-2"
              style={{
                background: "#eaf2ff",
                color: "#0a57bb",
                fontSize: "12px",
              }}
            >
              {dates.length} Dates
            </span>
          </div>
        </div>

        <div className="card-body p-3 p-md-4">
          {loading && (
            <div className="text-center py-3">
              <Loading />
            </div>
          )}

          {!loading && error && (
            <div
              className="alert alert-danger d-flex flex-wrap justify-content-between align-items-center gap-2 mb-0"
              role="alert"
            >
              <span>
                <FiAlertCircle className="me-2" />
                Unable to load the date report. Please try again.
              </span>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={fetchDatesData}
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && dates.length === 0 && (
            <div className="text-center py-5">
              <FiCalendar size={38} className="text-secondary mb-3" />
              <h6 className="fw-bold text-secondary">No report dates found</h6>
              <p className="small text-muted mb-0">
                Try selecting a different reference date.
              </p>
            </div>
          )}

          {!loading && !error && dates.length > 0 && (
            <div
              className="row g-3"
              style={{ maxHeight: "220px", overflowY: "auto" }}
            >
              {/* Horizontally Scrollable Daily Cards */}
              <div
                className="d-flex gap-2 pb-3"
                style={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  whiteSpace: "nowrap",
                  scrollbarWidth: "thin",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                {dates.map((item, index) => {
                  const status = getStatus(item);
                  const active = isSelected(item);

                  return (
                    <div
                      key={`${item.date}-${index}`}
                      style={{
                        flex: "0 0 205px",
                        width: "205px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => handleDateSelect(item)}
                        aria-pressed={active}
                        className="card h-100 w-100 text-start"
                        style={{
                          border: active
                            ? "2px solid #47c20e"
                            : `1px solid ${status.border}`,
                          borderRadius: "10px",
                          background: active ? "#f1ffef" : "#fff",
                          boxShadow: active
                            ? "0 3px 10px rgba(10, 187, 78, 0.14)"
                            : "0 2px 5px rgba(15, 37, 42, 0.04)",
                          cursor: "pointer",
                          transition:
                            "box-shadow 0.2s ease, border-color 0.2s ease",
                          padding: 0,
                          whiteSpace: "normal",
                        }}
                      >
                        <div className="card-body p-2">
                          {/* Date Header */}
                          <div className="d-flex justify-content-between align-items-center gap-1 mb-2">
                            <div className="d-flex align-items-center gap-2">
                              <div
                                className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
                                style={{
                                  width: "30px",
                                  height: "30px",
                                  background: active
                                    ? "#d3f7d3"
                                    : status.background,
                                  color: active ? "#27bb0a" : status.color,
                                  fontSize: "16px",
                                }}
                              >
                                <MdDateRange />
                              </div>

                              <div>
                                <div
                                  className="fw-bold"
                                  style={{ color: "#16365c", fontSize: "12px" }}
                                >
                                  {/* {item.date} */}
                                  {item.date.slice(8, 10)}-
                                  {item.date.slice(5, 7)}-
                                  {item.date.slice(0, 4)}
                                </div>
                                <div
                                  className="text-muted"
                                  style={{ fontSize: "10px" }}
                                >
                                  Daily report
                                </div>
                              </div>
                            </div>

                            {active && (
                              <span
                                className="badge rounded-pill"
                                style={{
                                  background: "#27bb0a",
                                  color: "#fff",
                                  fontSize: "9px",
                                }}
                              >
                                Selected
                              </span>
                            )}
                          </div>

                          {/* Daily Statistics */}
                          <div className="d-flex flex-column gap-2">
                            <div className="d-flex align-items-center justify-content-between gap-1">
                              <span
                                className="text-secondary"
                                style={{ fontSize: "11px" }}
                              >
                                <GoDotFill className="me-1" />
                                Entries
                              </span>
                              <span
                                className="fw-bold"
                                style={{ color: "#16365c", fontSize: "12px" }}
                              >
                                {Number(item.no_of_entries) || 0}
                              </span>
                            </div>

                            <div className="d-flex align-items-center justify-content-between gap-1">
                              <span
                                className="text-secondary"
                                style={{ fontSize: "11px" }}
                              >
                                <FiClock className="me-1" />
                                Availed time
                              </span>
                              <span
                                className="fw-semibold text-dark"
                                style={{ fontSize: "11px" }}
                              >
                                {item.total_availed_time
                                  ? item.total_availed_time.slice(0, -3)
                                  : "—"}
                              </span>
                            </div>

                            <div className="d-flex align-items-center justify-content-between gap-1">
                              <span
                                className="text-secondary"
                                style={{ fontSize: "11px" }}
                              >
                                <FiLayers className="me-1" />
                                Blocks granted
                              </span>
                              <span
                                className="fw-semibold text-dark"
                                style={{ fontSize: "11px" }}
                              >
                                {item.block_granted ?? 0}
                              </span>
                            </div>
                          </div>

                          <hr className="my-2" style={{ opacity: 0.1 }} />

                          {/* Status */}
                          <div
                            className="d-flex align-items-center gap-2 rounded-2 px-2 py-2"
                            style={{
                              color: status.color,
                              background: status.background,
                              fontSize: "11px",
                              fontWeight: 600,
                            }}
                          >
                            {status.icon}
                            <span>{status.label}</span>
                          </div>
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        {/* Status legend */}
        {/* <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: "12px" }}>
        <div className="card-body py-3 px-3">
          <div className="d-flex flex-wrap align-items-center gap-3 gap-md-4">
            <span className="small fw-bold text-secondary">
              STATUS LEGEND
            </span>

            {[
              { label: "Completed", color: "#16a34a" },
              { label: "Pending", color: "#d97706" },
              { label: "Partially Updated", color: "#0284c7" },
              { label: "No Entries", color: "#94a3b8" },
            ].map((item) => (
              <div
                key={item.label}
                className="d-flex align-items-center gap-1"
              >
                <GoDotFill style={{ color: item.color, fontSize: "16px" }} />
                <span className="small text-secondary">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div> */}
      </div>
    </div>
  );
};

export default Dates;
